import {
  QuoteSortOption,
  QuoteStatus,
  QuoteType,
} from '@rematter/frontend-lib-api-client';

import { QuoteRecord } from '../types/quote';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseZonedDateTime,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const quotesListQueryAtom = createQueryAtomFactory(
  'brokerage.quotesList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    accountIds?: string[];
    facilityIds?: string[];
    types?: QuoteType[];
    statuses?: QuoteStatus[];
    limit?: number;
    offset?: number;
    sorting?: QuoteSortOption[];
  }) => ({
    quotes: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          accountIds: params.accountIds,
          facilityIds: params.facilityIds,
          types: params.types,
          statuses: params.statuses,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 20,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        type: true,
        status: true,
        account: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
        },
        subtotal: {
          amount: true,
          currencyCode: true,
        },
        validUntil: true,
        lineItems: {
          id: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { quotes: QuoteRecord[]; totalCount: number } => {
      if (!data?.quotes?.entries) {
        return { quotes: [], totalCount: 0 };
      }

      return {
        quotes: data.quotes.entries.map((quote) => ({
          id: quote.id,
          quoteNumber: quote.documentNumber,
          type: quote.type,
          status: quote.status,
          account: { id: quote.account.id, name: quote.account.name },
          facility: quote.facility ? { id: quote.facility.id } : null,
          subtotal: quote.subtotal,
          validUntil: maybeParseZonedDateTime(quote.validUntil) ?? null,
          lineItemCount: quote.lineItems?.length ?? 0,
          timestamps: {
            createdAt: parseZonedDateTime(quote.timestamps.createdAt),
            updatedAt: parseZonedDateTime(quote.timestamps.updatedAt),
          },
        })),
        totalCount: data.quotes.page?.total ?? 0,
      };
    },
  },
);
